import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Music, Calendar, MapPin, Building, User, Guitar, MessageSquare, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { PageBackground } from "@/components/ui/page-background";
import { format } from "date-fns";
import { instrumentOptions } from "@shared/schema";
import { Squircle } from "@/components/ui/squircle";

interface EventDetails {
  id: number;
  name: string;
  type: "jam" | "openmic" | "generic";
  date: string;
  venue: string;
  description: string;
  organisationName: string;
  organisationLogoUrl?: string | null;
  eventLogoUrl?: string | null;
  signupLimit?: number | null;
  currentParticipants?: number;
}

interface SignupFormData {
  name: string;
  names?: string[]; // For generic events - multiple names
  instruments?: string[];
  performerType?: "solo" | "band";
  notes?: string;
}

async function fetchEventDetails(token: string): Promise<EventDetails> {
  const response = await fetch(`/api/public/signup/${token}`);
  if (!response.ok) {
    throw new Error(response.status === 404 ? 'Invalid or expired signup link' : 'Failed to load event details');
  }
  return response.json();
}

async function submitSignup(token: string, data: SignupFormData): Promise<any> {
  const response = await fetch(`/api/public/signup/${token}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to register for event');
  }
  
  return response.json();
}

export default function SignupPage() {
  const params = useParams();
  const token = params.token as string;
  const { toast } = useToast();

  // Function to determine which logo to use (event -> org -> default)
  const getLogoInfo = (eventDetails: EventDetails) => {
    if (eventDetails.eventLogoUrl) {
      return {
        src: eventDetails.eventLogoUrl,
        alt: `${eventDetails.name} Logo`,
        isSquircle: true
      };
    }
    if (eventDetails.organisationLogoUrl) {
      return {
        src: eventDetails.organisationLogoUrl,
        alt: `${eventDetails.organisationName} Logo`,
        isSquircle: true
      };
    }
    return {
      src: "/jam-jar.png",
      alt: "Jam Jar Logo",
      isSquircle: false
    };
  };
  
  const [formData, setFormData] = useState<SignupFormData>({
    name: "",
    names: [""],
    instruments: [],
    performerType: "solo",
    notes: "",
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { data: eventDetails, isLoading, error } = useQuery<EventDetails>({
    queryKey: [`/api/public/signup/${token}`],
    queryFn: () => fetchEventDetails(token),
    retry: false,
  });

  const signupMutation = useMutation({
    mutationFn: (data: SignupFormData) => submitSignup(token, data),
    onSuccess: () => {
      setIsSubmitted(true);
      toast({
        title: "Registration successful!",
        description: "You have been registered for the event.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Registration failed",
        description: error.message || "Failed to register for event",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // For generic events, validate names array
    if (eventDetails?.type === "generic") {
      const validNames = formData.names?.filter(name => name.trim()) || [];
      if (validNames.length === 0) {
        toast({
          title: "Please fill in all required fields",
          description: "At least one name is required",
          variant: "destructive",
        });
        return;
      }
      
      // Check if trying to register more participants than available slots
      if (eventDetails.signupLimit) {
        const remainingSlots = eventDetails.signupLimit - (eventDetails.currentParticipants || 0);
        if (validNames.length > remainingSlots) {
          toast({
            title: "Too many participants",
            description: `Only ${remainingSlots} slot(s) remaining. Please remove ${validNames.length - remainingSlots} participant(s).`,
            variant: "destructive",
          });
          return;
        }
      }
    } else {
      // For other event types, validate single name
      if (!formData.name.trim()) {
        toast({
          title: "Please fill in all required fields",
          description: "Name is required",
          variant: "destructive",
        });
        return;
      }
    }
    
    // For jam events, at least one instrument is required
    if (eventDetails?.type === "jam") {
      if (!formData.instruments || formData.instruments.length === 0) {
        toast({
          title: "Please fill in all required fields",
          description: "Please select at least one instrument for jam nights",
          variant: "destructive",
        });
        return;
      }
    }
    
    // For open mic events, performer type is required
    if (eventDetails?.type === "openmic" && !formData.performerType) {
      toast({
        title: "Please fill in all required fields", 
        description: "Performer type is required for open mic events",
        variant: "destructive",
      });
      return;
    }
    
    signupMutation.mutate(formData);
  };

  const handleInputChange = (field: keyof SignupFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <PageBackground>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-slate-700" />
            <p className="text-slate-700">Loading event details...</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (error || !eventDetails) {
    return (
      <PageBackground>
        <div className="flex items-center justify-center min-h-screen">
          <Card className="w-full max-w-md mx-4 bg-white/20 backdrop-blur-xl border-white/20">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-6 mt-4">
                <img src="/jam-jar.png" alt="Jam Jar Logo" className="h-48 w-48 drop-shadow-md"/>
              </div>

              <CardTitle className="text-slate-900 pb-2">Sign Up Disabled</CardTitle>

              <CardDescription className="text-slate-700">
                This may be because the event has already happened, signups have been
                turned off by the organiser or the number of signups were limited. If you think this is incorrect, contact the event organiser for a
                new link.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </PageBackground>
    );
  }

  if (isSubmitted) {
    return (
      <PageBackground>
        <div className="flex items-center justify-center min-h-screen">
          <Card className="w-full max-w-md mx-4 bg-white/20 backdrop-blur-xl border-white/20">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-6 mt-4">
                {(() => {
                  const logoInfo = getLogoInfo(eventDetails);
                  return logoInfo.isSquircle ? (
                    <Squircle 
                      src={logoInfo.src} 
                      alt={logoInfo.alt} 
                      size={192}
                      className="drop-shadow-md"
                    />
                  ) : (
                    <img src={logoInfo.src} alt={logoInfo.alt} className="h-48 w-48 drop-shadow-md"/>
                  );
                })()}
              </div>
              <CardTitle className="text-slate-900">Registration Complete!</CardTitle>
              <CardDescription className="text-slate-700">
                You have successfully registered for <strong>{eventDetails.name}</strong>. 
                We'll see you there!
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Event Details Card */}
          <Card className="mb-8 bg-white/20 backdrop-blur-xl border-white/20">
            <CardHeader>
              <div className="flex items-center gap-3 mb-4">
                {(() => {
                  const logoInfo = getLogoInfo(eventDetails);
                  return logoInfo.isSquircle ? (
                    <Squircle 
                      src={logoInfo.src} 
                      alt={logoInfo.alt} 
                      size={64}
                      className="drop-shadow-md flex-shrink-0"
                    />
                  ) : (
                    <img src={logoInfo.src} alt={logoInfo.alt} className="h-16 w-16 drop-shadow-md flex-shrink-0"/>
                  );
                })()}
                <div>
                  <CardTitle className="text-slate-900">{eventDetails.name}</CardTitle>
                  <CardDescription className="text-slate-700">
                  Hosted by {eventDetails.organisationName}
                  </CardDescription>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 text-sm text-slate-700">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {format(new Date(eventDetails.date), "EEEE, MMMM d, yyyy 'at' h:mm a")}
                </div>
                {eventDetails.venue && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {eventDetails.venue}
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 mt-4">
                <Badge
                  variant="secondary"
                  className={`${
                    eventDetails.type === "jam" 
                      ? "bg-blue-100/60 border-blue-200/50 text-blue-800" 
                      : eventDetails.type === "openmic"
                      ? "bg-purple-100/60 border-purple-200/50 text-purple-800"
                      : "bg-green-100/60 border-green-200/50 text-green-800"
                  }`}
                >
                  {eventDetails.type === "jam" ? "Jam Night" : 
                   eventDetails.type === "openmic" ? "Open Mic" : "Event"}
                </Badge>
              </div>
              
              {eventDetails.description && (
                <CardDescription className="mt-4 text-slate-700">
                  {eventDetails.description}
                </CardDescription>
              )}
            </CardHeader>
          </Card>

          {/* Signup Form */}
          <Card className="bg-white/20 backdrop-blur-xl border-white/20">
            <CardHeader>
              <CardTitle className="text-slate-900">Sign Up</CardTitle>
              <CardDescription className="text-slate-700">
                {eventDetails.type === "generic" 
                  ? "Add multiple participants in one submission"
                  : "Fill out the form below to register for this event"
                }
                {eventDetails.signupLimit && (
                  <div className="mt-2 text-sm">
                    <strong>
                      {eventDetails.currentParticipants || 0} of {eventDetails.signupLimit} spots filled
                      {eventDetails.type === "generic" && (
                        <span className="text-green-600 ml-2">
                          ({eventDetails.signupLimit - (eventDetails.currentParticipants || 0)} remaining)
                        </span>
                      )}
                    </strong>
                  </div>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {eventDetails.type === "generic" ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-900">
                        <User className="h-4 w-4 inline mr-2" />
                        Participant Names *
                      </Label>
                      {eventDetails.signupLimit && (
                        <div className="text-sm text-slate-600">
                          {(formData.names?.filter(name => name.trim()).length || 0)} of {eventDetails.signupLimit - (eventDetails.currentParticipants || 0)} available slots
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      {formData.names?.map((name, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={name}
                            onChange={(e) => {
                              const newNames = [...(formData.names || [])];
                              newNames[index] = e.target.value;
                              handleInputChange("names", newNames);
                            }}
                            placeholder={`Participant ${index + 1} name`}
                            className="bg-white/40 border-white/30 flex-1"
                          />
                          {(formData.names?.length || 0) > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newNames = formData.names?.filter((_, i) => i !== index) || [];
                                handleInputChange("names", newNames);
                              }}
                              className="bg-white/40 border-white/30"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      {(() => {
                        const remainingSlots = eventDetails.signupLimit 
                          ? eventDetails.signupLimit - (eventDetails.currentParticipants || 0)
                          : Infinity;
                        const currentNames = (formData.names?.length || 0);
                        const canAddMore = !eventDetails.signupLimit || currentNames < remainingSlots;
                        
                        return (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newNames = [...(formData.names || []), ""];
                              handleInputChange("names", newNames);
                            }}
                            disabled={!canAddMore}
                            className="w-full bg-white/40 border-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            {canAddMore 
                              ? "Add Another Participant" 
                              : `Limit Reached (${remainingSlots} max)`
                            }
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-900">
                        <User className="h-4 w-4 inline mr-2" />
                        Your Name *
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        placeholder="Enter your name"
                        className="bg-white/40 border-white/30"
                        required
                      />
                    </div>
                    
                    {eventDetails.type === "jam" ? (
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-slate-900">
                          <Guitar className="h-4 w-4 inline mr-2" />
                          Instruments *
                        </Label>
                        <div className="grid grid-cols-2 gap-2 p-4 bg-white/20 rounded-lg border border-white/30">
                          {instrumentOptions.map((instrument) => (
                            <div key={instrument} className="flex items-center space-x-2">
                              <Checkbox
                                id={`instrument-${instrument}`}
                                checked={formData.instruments?.includes(instrument) || false}
                                onCheckedChange={(checked) => {
                                  const currentInstruments = formData.instruments || [];
                                  const newInstruments = checked 
                                    ? [...currentInstruments, instrument]
                                    : currentInstruments.filter(i => i !== instrument);
                                  handleInputChange("instruments", newInstruments);
                                }}
                              />
                              <Label htmlFor={`instrument-${instrument}`} className="text-sm capitalize text-slate-700">
                                {instrument}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : eventDetails.type === "openmic" ? (
                      <div className="space-y-2">
                        <Label htmlFor="performerType" className="text-slate-900">
                          <Music className="h-4 w-4 inline mr-2" />
                          Performer Type *
                        </Label>
                        <Select
                          value={formData.performerType}
                          onValueChange={(value: any) => handleInputChange("performerType", value)}
                        >
                          <SelectTrigger className="bg-white/40 border-white/30">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="solo">Solo</SelectItem>
                            <SelectItem value="band">Band</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ) : null}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-slate-900">
                    <MessageSquare className="h-4 w-4 inline mr-2" />
                    Additional Notes
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange("notes", e.target.value)}
                    placeholder="Any additional information you'd like to share..."
                    className="bg-white/40 border-white/30 min-h-[80px]"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600/80 hover:bg-blue-700/90 text-white"
                  disabled={signupMutation.isPending}
                >
                  {signupMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Registering...
                    </>
                  ) : eventDetails.type === "generic" ? (
                    `Register ${(formData.names?.filter(name => name.trim()).length || 0)} Participant${(formData.names?.filter(name => name.trim()).length || 0) !== 1 ? 's' : ''}`
                  ) : (
                    "Register for Event"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageBackground>
  );
}